import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Phone, Mail, Instagram, Facebook, MessageCircle,
  ShieldCheck, Lock, CreditCard, Truck, BadgeCheck, ArrowRight,
} from "lucide-react";

const WHATS = "https://wa.me/5519992923017?text=Ol%C3%A1%20Biofarmax!";

export default function Footer() {
  return (
    <footer className="bg-brand-dark text-white/90 mt-24" data-testid="footer">
      {/* SECURITY STRIP */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { Icon: Lock,        title: "Compra segura",    text: "SSL + checkout protegido" },
            { Icon: ShieldCheck, title: "Qualidade CRF",    text: "Farmacêutico responsável" },
            { Icon: Truck,       title: "Entrega rápida",   text: "Para todo o Brasil" },
            { Icon: CreditCard,  title: "PIX 5% OFF",       text: "Ou até 6× sem juros" },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`footer-trust-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 grid place-items-center flex-shrink-0">
                <b.Icon size={18} className="text-brand-accent" strokeWidth={1.6} />
              </div>
              <div>
                <div className="font-tech text-sm font-semibold">{b.title}</div>
                <div className="text-xs text-white/55">{b.text}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN */}
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        {/* BRAND + NEWSLETTER */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <img src="/assets/biofarmax-logo.png" alt="Biofarmax" className="w-12 h-12 object-contain bg-white rounded-xl p-1" />
            <div>
              <div className="font-heading text-2xl leading-none">Biofarmax</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">Farmácia · Manipulação</div>
            </div>
          </div>
          <p className="text-sm text-white/65 leading-relaxed mb-6 max-w-md">
            Farmácia de manipulação com excelência farmacêutica, atendimento personalizado e qualidade premium na Região Metropolitana de Campinas — agora atendendo todo o Brasil.
          </p>

          {/* Newsletter */}
          <form
            className="flex items-stretch gap-2 mb-6 max-w-md"
            onSubmit={(e) => e.preventDefault()}
            data-testid="footer-newsletter"
          >
            <input
              type="email"
              placeholder="Seu e-mail para 5% OFF"
              className="flex-1 px-4 py-3 rounded-full bg-white/8 border border-white/15 focus:border-brand-accent focus:outline-none font-tech text-sm placeholder:text-white/40"
              data-testid="footer-newsletter-email"
            />
            <button type="submit" className="bg-brand-accent hover:bg-brand-accentHover text-white px-5 py-3 rounded-full font-tech text-sm flex items-center gap-1 transition" data-testid="footer-newsletter-submit">
              Assinar <ArrowRight size={14} />
            </button>
          </form>

          <div className="flex gap-3">
            <a href={WHATS} target="_blank" rel="noreferrer" className="p-2.5 border border-white/15 rounded-full hover:bg-brand-accent hover:border-brand-accent transition" data-testid="footer-whatsapp" aria-label="WhatsApp">
              <MessageCircle size={18} strokeWidth={1.5} />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="p-2.5 border border-white/15 rounded-full hover:bg-brand-accent hover:border-brand-accent transition" data-testid="footer-instagram" aria-label="Instagram">
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" className="p-2.5 border border-white/15 rounded-full hover:bg-brand-accent hover:border-brand-accent transition" data-testid="footer-facebook" aria-label="Facebook">
              <Facebook size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* LINK COLUMNS */}
        <div className="md:col-span-2">
          <h4 className="font-tech uppercase text-xs tracking-[0.25em] text-brand-accent mb-5">Loja</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/loja" className="text-white/75 hover:text-brand-accent transition">Todos os produtos</Link></li>
            <li><Link to="/categoria/biobeauty" className="text-white/75 hover:text-brand-accent transition">Biobeauty</Link></li>
            <li><Link to="/categoria/saude" className="text-white/75 hover:text-brand-accent transition">Saúde</Link></li>
            <li><Link to="/categoria/fitness" className="text-white/75 hover:text-brand-accent transition">Fitness</Link></li>
            <li><Link to="/categoria/bem-estar" className="text-white/75 hover:text-brand-accent transition">Bem-Estar</Link></li>
            <li><Link to="/categoria/pele" className="text-white/75 hover:text-brand-accent transition">Pele</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-tech uppercase text-xs tracking-[0.25em] text-brand-accent mb-5">Institucional</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/sobre" className="text-white/75 hover:text-brand-accent transition">Sobre nós</Link></li>
            <li><Link to="/politica-privacidade" className="text-white/75 hover:text-brand-accent transition">Privacidade</Link></li>
            <li><Link to="/politica-trocas" className="text-white/75 hover:text-brand-accent transition">Trocas e devoluções</Link></li>
            <li><Link to="/rastrear" className="text-white/75 hover:text-brand-accent transition">Rastrear pedido</Link></li>
            <li><Link to="/sobre#farmaceutico-responsavel" className="text-white/75 hover:text-brand-accent transition">Farmacêutico responsável</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-tech uppercase text-xs tracking-[0.25em] text-brand-accent mb-5">Contato</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <MapPin size={16} strokeWidth={1.5} className="mt-0.5 text-brand-accent flex-shrink-0" />
              <span className="text-white/75">Região Metropolitana<br/>de Campinas — SP</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone size={16} strokeWidth={1.5} className="text-brand-accent flex-shrink-0" />
              <a href={WHATS} target="_blank" rel="noreferrer" className="text-white/75 hover:text-brand-accent transition">(19) 99292-3017</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} strokeWidth={1.5} className="text-brand-accent flex-shrink-0" />
              <a href="mailto:contato@biofarmax.com.br" className="text-white/75 hover:text-brand-accent transition">contato@biofarmax.com.br</a>
            </li>
          </ul>
        </div>
      </div>

      {/* LEGAL STRIP */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/45">
          <div className="flex flex-wrap gap-x-6 gap-y-1.5 items-center">
            <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-brand-accent" /> CNPJ: 01.853.857/0001-17</span>
            <span>CRF-SP: 21860</span>
            <span>Farm. Resp.: Reinaldo Stefani Neto</span>
          </div>
          <span>© {new Date().getFullYear()} Biofarmax · Todos os direitos reservados.</span>
        </div>
      </div>

      {/* Floating WhatsApp mascot (desktop only — mobile uses sticky CTA) */}
      <a
        href={WHATS}
        target="_blank"
        rel="noreferrer"
        className="hidden md:block fixed bottom-6 right-6 z-40 group"
        aria-label="WhatsApp"
        data-testid="floating-whatsapp"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-30 blur-xl group-hover:opacity-60 transition-opacity" />
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
        <span className="relative block w-20 h-20 rounded-full bg-gradient-to-br from-[#F7F2E8] to-[#EFE6D5] shadow-[0_20px_50px_-15px_rgba(37,211,102,0.6)] ring-1 ring-[#25D366]/30 group-hover:scale-110 transition-transform duration-300 overflow-hidden">
          <img
            src="/assets/whatsapp-pharmacist.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center scale-110 drop-shadow-[0_4px_8px_rgba(0,0,0,0.15)]"
          />
        </span>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-brand-dark text-white text-xs font-tech px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-all duration-300 shadow-lg pointer-events-none">
          Fale Conosco
        </span>
      </a>
    </footer>
  );
}
